import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { BehaviorSubject, Subscription, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { StudyGroupInfo, SampleTrackerData } from '../group-info/study-group-info';
import { SampleStatusInfo } from './../dashboard/overview-dashboard/sample-status/sample-status-info';
import { SampleTrackingStorage } from '../sample-tracking-storage';
import { ProtocolFilterData, SampleTrackingSampleCountRequest, SelectedFilter, FilterNames, FilterTypeEnum } from './../dashboard/overview-dashboard/dashboard-info';
import { IFilterSelectItem } from './../shared/sample-tracking-select/sample-tracking-select.component';
import { SampleTrackingSelectFilterService } from './../shared/sample-tracking-select/sample-tracking-select-filter-service';
import { DateRangeFilterValue } from './../../shared/time-frame-filter/timeframe-filter.component';
import { FilterSamplesInfo, GroupTrackingGridData } from './filter-samples-info';
import { SampleColumnsService } from './column/sample-column-service';
import { ActivityLogger } from '../../activities/activity-logger';
import { UrlBuilder } from '../../core';
import { IBreadcrumb, BreadcrumbService } from '../shared/breadcrumb-service/breadcrumb.service';
import { GridEvent, GridHeader, GridDetails } from '../tracking-overview/interfaces/grid';
import { FormControl } from '@angular/forms';
import { SamplesHelperService } from './service/samples-helper-service';
import { ExcludeFilterItems } from '../reports/interfaces/filter-data';
import { cloneDeep, uniq } from 'lodash';
import { TrackingGridService } from '../../shared/covance-grid/tracking-grid.service';
import { SamplesDataDownloadService } from './column/samples-data-download-service';
import { DynamicListViewColumnData, FormatedConfigInfo } from './interface/dynamic-list-info';

@Component({
    selector: 'dynamic-list-view',
    template: require('./st-filter-samples.component.html'),
    styles: [require('./st-filter-samples.component.scss')],
    providers: [SampleTrackingSelectFilterService, SampleColumnsService, TrackingGridService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicListViewComponent implements OnInit, OnDestroy {

    public columnDefs$: GridHeader[];
    public data$: ProtocolFilterData[];
    public dataList: ProtocolFilterData[];
    public selectedProtocol$: SampleTrackerData;
    public selectedStudyGroup$: StudyGroupInfo;
    public isProtocol = false;
    public loadingComplete = false;
    public defaultProtocol: IFilterSelectItem;
    public gridDetails: GridDetails;
    public gridLoadingComplete = false;
    public allCountries$: IFilterSelectItem[];
    public projects$: IFilterSelectItem[];
    public regions$: IFilterSelectItem[];
    public invNumber$: IFilterSelectItem[];
    public protocol: IFilterSelectItem[];
    public protocolFilters: IFilterSelectItem[];
    public selectedFilterList: SelectedFilter[] = [];
    public searchTerm = new FormControl('');
    public sampleStatusList: SampleStatusInfo[];
    public selectedStatus: IFilterSelectItem;
    public selectedDateRange: DateRangeFilterValue;
    public gridData$: GroupTrackingGridData[];
    public formattedGridData: GroupTrackingGridData[];
    public pagePerItems = [25, 50, 75, 100, 250, 500];
    public defaultPageSize = 25;
    public defaultProtocolFilter: IFilterSelectItem[] = [];
    public defaultProjectFilter: IFilterSelectItem[] = [];
    public defaultRegionFilter: IFilterSelectItem[] = [];
    public defaultCountryFilter: IFilterSelectItem[] = [];
    public defaultInvestigatorFilter: IFilterSelectItem[] = [];
    public currentFilter: FilterTypeEnum = FilterTypeEnum.protocol;
    public previousRequestData: SampleTrackingSampleCountRequest;
    public filterSamplesInfo: FilterSamplesInfo;
    public defaultQuickFilter: string;
    public loadingText = 'Loading Column View Data...';
    public defaultOrderByField = 'accessionnumber';
    public patientNumber: string;
    public timeframeOptions: IFilterSelectItem[] = [];
    public selectedTimeFrame: IFilterSelectItem[] = [];
    public dynamicListColumnInfo: DynamicListViewColumnData;
    public collpaseFilter = false;
    public gridCount = 0;
    public showExportMenu = false;
    public isDynamicView = true; public isMissMatchOccured = false; public cellToolTipText = '';
    private gridDataSubscription: Subscription;
    private searchSubscription: Subscription;
    private expandFilter = false;
    private timeLeft: number;
    private interval: any;
    private columnSwapped = false;
    constructor(
        private storage: SampleTrackingStorage,
        private activatedRoute: ActivatedRoute,
        private selectFilterService: SampleTrackingSelectFilterService,
        private sampleColumnService: SampleColumnsService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly urlBuilder: UrlBuilder,
        private breadcrumbService: BreadcrumbService,
        private samplesHelperService: SamplesHelperService,
        activityLogger: ActivityLogger,
        private downloadService: SamplesDataDownloadService,
    ) {
        let filterInfo = localStorage.getItem('listViewData');
        if (!filterInfo) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        }
        this.calculateCollapsingOnResize();
        this.filterSamplesInfo = JSON.parse(filterInfo); this.filterSamplesInfo.selectedStatus.disclaimerText ? this.samplesHelperService.alignBreadcrumb('60px') : this.samplesHelperService.alignBreadcrumb('unset');
        this.initializePageData();
        this.protocol = this.loadProtocolData();
        this.dataList = this.filterSamplesInfo.filterData;
        if (!this.dataList || this.dataList.length === 0) {
            this.samplesHelperService.showNoDataText();
        }
        this.selectedFilterList = this.filterSamplesInfo.selectedFilterItems;
        this.setFilterData(this.dataList);
        this.defaultProtocolFilter = this.isProtocol && this.dataList && this.dataList.length > 0 ? this.protocol : [];
        const searchTermFlow = merge(
            of(''),
            this.searchTerm.valueChanges
                .pipe(
                    debounceTime(300),
                    distinctUntilChanged()
                )
        );
        this.searchSubscription = searchTermFlow.subscribe((term) => {
            this.applyGridSearch(term);
            this.changeDetectorRef.markForCheck();
        });
        activityLogger.logActivity('Access Sample Tracking List View');
    }

    public ngOnInit() {
        this.filterSamplesInfo.selectedFilterItems.map(filterItem => {
            switch (filterItem.label) {
                case FilterNames.protocol:
                    this.onProtocolFilterChanged(filterItem.filterItems);
                    break;
                case FilterNames.region:
                    this.onRegionFilterChanged(filterItem.filterItems);
                    break;
                case FilterNames.project:
                    this.onProjectFilterChanged(filterItem.filterItems);
                    break;
                case FilterNames.country:
                    this.onCountryFilterChanged(filterItem.filterItems);
                    break;
                case FilterNames.investigatorNum:
                    this.onInvestigatorNameFilterChanged(filterItem.filterItems);
                    break;
            }
        });
        this.selectedStatus = this.filterSamplesInfo.selectedStatus;
        this.selectedDateRange = this.filterSamplesInfo.selectedDateRange;
        if (this.selectedStatus.isQuickFilterEnabled) {
            this.defaultQuickFilter = this.selectedStatus.subValue;
        }
        if (this.selectedStatus.filterByPatient) {
            this.patientNumber = this.selectedStatus.filterByPatient.value;
        }
        this.getDynamicGridColumns();
    }

    @HostListener('window:resize')
    public onWindowResize() {
        this.calculateCollapsingOnResize();
    }

    public ngOnDestroy() {
        clearInterval(this.interval);
        this.gridLoadingComplete = true;
        this.samplesHelperService.closeConfirmDialog();
        if (!this.gridDataSubscription) { return; }
        this.gridDataSubscription.unsubscribe();
        this.searchSubscription.unsubscribe(); this.samplesHelperService.alignBreadcrumb('unset');
    }

    public get isDrawerCollapsed(): boolean {
        return this.expandFilter;
    }

    public initializePageData() {
        let id = 0;
        this.activatedRoute.params.subscribe(param => {
            id = Number(param.studyGroupId);
            if (param.type === 'study-group') {
                this.selectedStudyGroup$ = this.filterSamplesInfo.selectedStudyGroup;
            }
            if (param.type === 'protocol') {
                this.isProtocol = true;
                this.selectedProtocol$ = this.filterSamplesInfo.selectedProtocol$;
            }
        });
        if ((this.isProtocol && this.selectedProtocol$.protocolFk !== id) ||
            (!this.isProtocol && this.selectedStudyGroup$.groupPk !== id)) {
            this.urlBuilder.esa().sampletracking().home().navigate();
            return;
        }
        let routerUrl: IBreadcrumb = {
            label: 'Filter Samples',
            url: this.isProtocol ? this.urlBuilder.esa().sampletracking().protocolInfo(this.selectedProtocol$).filterSamples(this.filterSamplesInfo.selectedStatus.key).url() :
                this.urlBuilder.esa().sampletracking().studyGroup(this.selectedStudyGroup$).filterSamples(this.filterSamplesInfo.selectedStatus.key).url()
        };
        this.breadcrumbService.AddRouterToBreadCrumb(routerUrl);
    }

    public applyFilter() {
        this.onCollapseButton();
        this.selectedFilterList = [];
        this.selectedFilterList = this.samplesHelperService.loadSelectedFilterList(this.defaultProtocolFilter, this.defaultRegionFilter, this.defaultProjectFilter,
                                                                                   this.defaultCountryFilter, this.defaultInvestigatorFilter);
        this.loadGridData();
    }

    public getFilterDefaultValues(filterSubject: BehaviorSubject<IFilterSelectItem[]>) {
        const selectedFilters = filterSubject.getValue();
        if (selectedFilters && selectedFilters.length > 0) { return selectedFilters; }
        return null;
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        if ($event.dateBegin === this.selectedDateRange.dateBegin
            && $event.dateEnd === this.selectedDateRange.dateEnd && $event.rangeType !== 'dateRange') { return; }
        this.selectedDateRange = $event;
        this.filterSamplesInfo.selectedDateRange = this.selectedDateRange;
        this.loadGridData();
    }

    public applyGridSearch(term: string) {
        if (!this.formattedGridData) { return; }
        this.gridDetails = null;
        this.gridLoadingComplete = false;
        let filteredData = this.sampleColumnService.applySearch(term, this.formattedGridData, this.columnDefs$);
        this.gridDetails = { header: this.columnDefs$, data: filteredData };
        this.gridLoadingComplete = true;
    }

    public get getSelectedProtocolList() {
        if (!this.dataList || this.dataList.length === 0) { return []; }
        return this.defaultProtocolFilter && this.defaultProtocolFilter.length > 0 ? this.defaultProtocolFilter : this.protocolFilters;
    }

    public clearFilter() {
        this.onCollapseButton();
        this.defaultProtocolFilter = [];
        this.defaultRegionFilter = [];
        this.defaultProjectFilter = [];
        this.defaultCountryFilter = [];
        this.defaultInvestigatorFilter = [];
        this.setFilterData(this.dataList);
        this.selectedFilterList = [];
        this.selectFilterService.clearFilterSelection(true);
        this.patientNumber = null;
        this.loadGridData();
    }

    public onCountryFilterChanged($event: IFilterSelectItem[]) {
        this.defaultCountryFilter = $event;
        let filterData = this.samplesHelperService.getFilterData(this.defaultProtocolFilter, $event, this.defaultRegionFilter, this.defaultProjectFilter, [], this.dataList);
        this.setFilterData(filterData, { protocol: true, project: true, region: true, country: true });
    }

    public onProjectFilterChanged($event: IFilterSelectItem[]) {
        this.defaultProjectFilter = $event;
        let filterData = this.samplesHelperService.getFilterData(this.defaultProtocolFilter, [], this.defaultRegionFilter, $event, [], this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true, project: true });
    }

    public onRegionFilterChanged($event: IFilterSelectItem[]) {
        this.defaultRegionFilter = $event;
        let filterData = this.samplesHelperService.getFilterData(this.defaultProtocolFilter, [], $event, [], [], this.dataList);
        this.setFilterData(filterData, { protocol: true, region: true });
    }

    public onProtocolFilterChanged($event: IFilterSelectItem[]) {
        this.defaultProtocolFilter = $event;
        let filterData = this.samplesHelperService.getDropdownFilterData(this.dataList, $event, 'protocolCode');
        this.setFilterData(filterData, { protocol: true });
    }

    public onInvestigatorNameFilterChanged($event: IFilterSelectItem[]) {
        this.defaultInvestigatorFilter = $event;
        let filterData = this.samplesHelperService.getFilterData(
            this.defaultProtocolFilter, this.defaultCountryFilter, this.defaultRegionFilter,
            this.defaultProjectFilter, $event, this.dataList);
        this.setFilterData(filterData, { protocol: true, project: true, region: true, country: true, investigator: true });
    }
    public onQuickFilterChanged($event: string) {
        this.loadingText = 'Fetching data...';
        this.gridLoadingComplete = false;
        if ($event === this.defaultQuickFilter) {
            this.defaultQuickFilter = '';
        } else {
            this.defaultQuickFilter = $event;
        }
        this.formatData(this.gridData$, false);
        this.gridLoadingComplete = true;
        this.changeDetectorRef.markForCheck();
    }
    public onCollapseButton() {
        this.expandFilter = !this.expandFilter;
    }

    public onGridItemClick(selectedItem: GridEvent) {
        this.sampleColumnService.onGridItemClickReceivedList(selectedItem, this.dynamicListColumnInfo, this.gridData$);
    }
    public async onEditViewClick() {
        let columnSettingInfo = this.sampleColumnService.getColumnSettingInfo(this.columnDefs$, this.dynamicListColumnInfo);
        const result = await this.samplesHelperService.openEditDialog(columnSettingInfo).toPromise();
        if (result) {
            let isColumnUpdated = this.sampleColumnService.isColumnUpdated(columnSettingInfo, result);
            if (!isColumnUpdated && !result.resetSettings) { return; }
            let data: FormatedConfigInfo = await this.sampleColumnService.loadGridColumnOnColumnSettings(result, this.dynamicListColumnInfo, this.columnDefs$);
            if (!data.isDeleted && result.resetSettings) { return; }
            if (data.userConfig) {
                this.dynamicListColumnInfo.configJSON = JSON.stringify(data.userConfig);
            }
            this.columnDefs$ = data.columns;
            this.applyFilter();
            this.expandFilter = false;
            this.columnSwapped = false;
        }
    }
    public exportXlsx(downloadAsCSV: boolean) {
        this.showExportMenu = false;
        let clonedGridData = cloneDeep(this.gridData$);
        if (this.defaultQuickFilter) {
            if (this.selectedStatus.enableShippedToFilter) { clonedGridData = this.defaultQuickFilter === 'Labcorp' ? clonedGridData.filter(t => t.shippedTo !== null) : clonedGridData.filter(t => t.shippedTo === null); }
            if (this.selectedStatus.enableReturnTypeFilter) { clonedGridData = this.defaultQuickFilter === 'Optional' ? clonedGridData.filter(t => t.returntype !== 'Optional') : clonedGridData.filter(t => t.returntype === 'Required'); }
        }
        let fileName = this.samplesHelperService.getExportFileName(this.isProtocol, this.selectedProtocol$, this.selectedStudyGroup$, this.selectedStatus);
        this.downloadService.exportData(this.columnDefs$, clonedGridData, fileName, this.selectedStatus.id, downloadAsCSV, true);
    }

    public onTimeFrameOptionSelected($event: IFilterSelectItem[]) {
        if (this.selectedTimeFrame[0].key === $event[0].key) { return; }
        this.selectedTimeFrame = $event;
        this.loadGridData();
    }
    public onColumnOrderChange($event: GridHeader[]) {
        this.columnSwapped = this.sampleColumnService.isColumnSwapped(this.columnDefs$, $event);
        this.columnDefs$ = $event;
        if (!this.columnSwapped) { return; }
        let configData = this.sampleColumnService.loadGridColumnOnColumnSwap(this.columnDefs$, this.dynamicListColumnInfo);
        this.dynamicListColumnInfo.configJSON = JSON.stringify(configData);
    }
    public openMisMatchModel($event: any) {
        this.samplesHelperService.openMisMatchModel($event, this.dynamicListColumnInfo.dynamicListColumnInfo);
    }
    private getDynamicGridColumns() {
        var statusKey = this.selectedStatus ? this.selectedStatus.viewName : '';
        this.storage.getDynamicListViewColumnInfo(statusKey).subscribe((info: DynamicListViewColumnData) => {
            let formatedData: any = this.sampleColumnService.getGridColumnInfo(info);
            this.columnDefs$ = formatedData.columnHeader;
            this.dynamicListColumnInfo = info;
            this.defaultPageSize = formatedData.pageSize ? formatedData.pageSize : this.defaultPageSize;
            if (formatedData.pageSize) {
                this.pagePerItems = [...this.pagePerItems, formatedData.pageSize];
                this.pagePerItems = uniq(this.pagePerItems.sort((a, b) => a < b ? -1 : 1));
            }
            this.timeframeOptions = this.sampleColumnService.getDateColumnsByColumnFilter(this.dynamicListColumnInfo.dynamicListColumnInfo);
            this.selectedTimeFrame = this.sampleColumnService.getDefaultTimeFrameOption(this.timeframeOptions, this.selectedStatus.id);
            this.loadGridData();
        });
    }   
    private loadProtocolData() {
        return !this.isProtocol ? this.getProtocolFilterList() : [{
            id: this.selectedProtocol$.protocolFk,
            value: this.selectedProtocol$.protocolCode,
            checked: true
        }];
    }

    private getProtocolFilterList() {
        const protocolList: IFilterSelectItem[] = [];
        for (var study of this.selectedStudyGroup$.studyList) {
            protocolList.push({ id: study.protocolPk, value: study.protocolCode });
        }
        return protocolList;
    }
    private async loadGridData() {
        this.loadingText = 'Fetching data...';
        this.isMissMatchOccured = false;
        this.changeDetectorRef.markForCheck();
        if (this.gridDataSubscription) {
            this.gridDataSubscription.unsubscribe();
        }
        if (!this.selectedDateRange) {
            this.selectedDateRange = this.sampleColumnService.getMonthlyRange();
        }
        let isOnHoldListData = this.selectedStatus.id === 9 ? true : false;
        let queryFilters = this.samplesHelperService.getWhereFilters(this.selectedFilterList, this.protocolFilters, this.selectedDateRange, this.selectedTimeFrame[0]);
        let dynamicListColumns = this.samplesHelperService.getDynamicListRequestInfo(queryFilters, this.columnDefs$, this.dynamicListColumnInfo);
        this.gridLoadingComplete = false; 
        this.gridData$ = null;
        if (this.interval) { 
            clearInterval(this.interval);
        }
        this.interval = setInterval(async () => { await this.checkIfLoaded(); }, 1000);
        this.timeLeft = 0;
        this.gridDataSubscription = this.storage.getDynamicListView(dynamicListColumns).subscribe((info) => {
            this.loadingText = 'Downloaded data, Processing ' + info.data.length + ' rows...';
            this.changeDetectorRef.markForCheck();
            this.samplesHelperService.closeConfirmDialog();
            this.timeLeft = 0;
            clearInterval(this.interval);
            let filteredData = this.samplesHelperService.filterDataByPatientNumber(this.patientNumber, info.data);
            this.formatData(filteredData, isOnHoldListData, info.isCached);
            setTimeout(() => { this.gridLoadingComplete = true; this.changeDetectorRef.markForCheck(); }, 0);
            this.changeDetectorRef.markForCheck();
            this.storage.clearGridData();
        });
        this.filterSamplesInfo.selectedFilterItems = this.selectedFilterList;
        localStorage.setItem('listViewData', JSON.stringify(this.filterSamplesInfo));
    }
    private formatData(data: GroupTrackingGridData[] | null | undefined, skipExpandData: boolean, isCacheData = false) {
        if (!data) {
            this.gridData$ = [];
            return;
        }
        this.gridData$ = data;
        if (this.defaultQuickFilter) {
            if (this.selectedStatus.enableShippedToFilter) { data = this.defaultQuickFilter === 'Labcorp' ? this.gridData$.filter(t => t.shippedTo !== null) : this.gridData$.filter(t => t.shippedTo === null); }
            if (this.selectedStatus.enableReturnTypeFilter) { data = this.defaultQuickFilter === 'Required' ? this.gridData$.filter(t => t.returntype === 'Required') : this.gridData$.filter(t => t.returntype === 'Optional'); }
        }
        if (!data || data.length <= 0) {
            this.gridDetails = { header: this.columnDefs$, data: [] };
        } else {
            this.samplesHelperService.isMissMatchOccured = false;
            this.formattedGridData = isCacheData ? data : this.samplesHelperService.formatGridData(data, skipExpandData, this.columnDefs$, true, this.filterSamplesInfo.selectedStatus.isMissMatchIconAppear);
            this.gridDetails = { header: this.columnDefs$, data: this.formattedGridData };
            this.isMissMatchOccured = Array.isArray(this.formattedGridData) && this.formattedGridData.some((gd: GroupTrackingGridData) => gd?.missMatches && Array.isArray(gd.missMatches) && gd.missMatches.length > 0);
            this.cellToolTipText = 'There is a difference in the value reported during sample registration and the value received at the lab. Please click on the icon to review the differences.';
        }
        this.gridCount = 0;
        skipExpandData ? data.forEach(d => { this.gridCount += +d.samplesEffectedByQuery; }) : this.gridCount = data.length;
    }
    private async checkIfLoaded() {
        this.timeLeft = this.timeLeft ? this.timeLeft + 1 : 1;
        if (this.timeLeft === 20 && !this.gridLoadingComplete && this.gridData$ === null) {
            clearInterval(this.interval);
            this.timeLeft = 0;
            let result = await this.samplesHelperService.displayConfirmDialog(this.isProtocol).toPromise();
            if (!result) { return; }
            if (result.isCancelled === true) {
                this.gridDataSubscription.unsubscribe();
                this.gridDetails = { header: this.columnDefs$, data: [] };
                this.gridLoadingComplete = true;
            }
            if (result.isCancelled === false) {
                this.interval = setInterval(async () => { await this.checkIfLoaded(); }, 1000);
            }
        }
        return;
    }
    private setFilterData(portfolioData: ProtocolFilterData[], exclude?: ExcludeFilterItems) {
        if (!portfolioData || !portfolioData.length && this.isProtocol && this.defaultProtocolFilter.length === 0) {
            return null;
        }
        if (!exclude || !exclude.protocol) {
            this.protocolFilters = this.samplesHelperService.loadDropDownItems(portfolioData, 'protocolId', 'protocolCode');
            this.defaultProtocolFilter = this.samplesHelperService.getFilteredItems(this.protocolFilters, this.defaultProtocolFilter);
            if (this.isProtocol) {
                this.defaultProtocolFilter = this.protocolFilters;
            }
        }
        if (!exclude || !exclude.region) {
            this.regions$ = this.samplesHelperService.loadDropDownItems(portfolioData, null, 'region');
            this.defaultRegionFilter = this.samplesHelperService.getFilteredItems(this.regions$, this.defaultRegionFilter);
            if (this.defaultRegionFilter && this.defaultRegionFilter.length > 0) {
                portfolioData = this.samplesHelperService.getDropdownFilterData(portfolioData, this.defaultRegionFilter, 'region');
            }
        }
        if (!exclude || !exclude.project) {
            this.projects$ = this.samplesHelperService.loadDropDownItems(portfolioData, 'subprotocolId', 'projectNumber');
            this.defaultProjectFilter = this.samplesHelperService.getFilteredItems(this.projects$, this.defaultProjectFilter);
            if (this.defaultProjectFilter && this.defaultProjectFilter.length > 0) {
                portfolioData = this.samplesHelperService.getDropdownFilterData(portfolioData, this.defaultProjectFilter, 'projectNumber');
            }
        }
        if (!exclude || !exclude.country) {
            this.allCountries$ = this.samplesHelperService.loadDropDownItems(portfolioData, 'countryId', 'countryName');
            this.defaultCountryFilter = this.samplesHelperService.getFilteredItems(this.allCountries$, this.defaultCountryFilter);
            if (this.defaultCountryFilter && this.defaultCountryFilter.length > 0) {
                portfolioData = this.samplesHelperService.getDropdownFilterData(portfolioData, this.defaultCountryFilter, 'countryName');
            }
        }
        if (!exclude || !exclude.investigator) {
            this.invNumber$ = this.samplesHelperService.loadDropDownItems(portfolioData, 'investigatorSiteId', 'investigatorNumber', 'investigatorName');
            this.defaultInvestigatorFilter = this.samplesHelperService.getFilteredItems(this.invNumber$, this.defaultInvestigatorFilter);
        }
        this.changeDetectorRef.markForCheck();
        return portfolioData;
    }
    private calculateCollapsingOnResize() {
        let zoom = ((window.outerWidth - 10) / window.innerWidth) * 100;
        this.collpaseFilter = zoom > 80 ? true : false;
    }
}